/**
 * Service Worker registration.
 *
 * Runs only in production builds (CRA does not serve /service-worker.js
 * during `yarn start`, and running an SW against the dev server would
 * cache stale hot-reloaded bundles).
 *
 * Skips registration on:
 *   - localhost / 127.0.0.1 (dev)
 *   - non-HTTPS origins (SW requires secure context)
 *
 * On a new version being detected, posts SKIP_WAITING immediately so the
 * user gets the fresh bundle on the next navigation, no manual refresh
 * needed.
 */
export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  const isLocalhost = ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
  const isSecure = window.location.protocol === "https:" || isLocalhost;
  if (!isSecure) return;
  // Skip in dev — the CRA dev server doesn't serve service-worker.js.
  if (process.env.NODE_ENV !== "production") return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((reg) => {
        // Auto-activate the new worker when it lands, no user prompt needed.
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              nw.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch((err) => {
        // Non-fatal — the app works fine without an SW, just no offline caching.
        // eslint-disable-next-line no-console
        console.warn("SW registration failed:", err);
      });

    // Reload once when a new SW takes control so the user gets the fresh
    // bundle without a manual refresh.
    let refreshed = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshed) return;
      refreshed = true;
      window.location.reload();
    });
  });
}
